import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSite } from '../context/SiteContext.jsx'
import { useToast } from '../context/ToastContext.jsx'

const blank = { imageUrl: '', mobileImageUrl: '', linkUrl: '', caption: '', utm: '', active: true, sortOrder: 100 }

export default function AdminBanners() {
  const { banners, addBanner, updateBanner, deleteBanner, uploadBannerImage, configured } = useSite()
  const toast = useToast()
  const [editing, setEditing] = useState(null) // banner | 'new' | null
  const [f, setF] = useState(blank)
  const [saving, setSaving] = useState(false)
  const [uploadingField, setUploadingField] = useState(null) // 'imageUrl' | 'mobileImageUrl' | null

  // One handler for both the desktop and mobile image pickers — pass the field it fills.
  const onPickFile = (field) => async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (!file) return
    setUploadingField(field)
    const res = await uploadBannerImage(file)
    setUploadingField(null)
    if (res?.error) return toast(res.error, 'red')
    setF((s) => ({ ...s, [field]: res.url }))
    toast('Image uploaded', 'green')
  }

  const startNew = () => { setF(blank); setEditing('new') }
  const startEdit = (b) => {
    setF({ imageUrl: b.image_url, mobileImageUrl: b.mobile_image_url || '', linkUrl: b.link_url || '', caption: b.caption || '', utm: b.utm || '', active: b.active, sortOrder: b.sort_order })
    setEditing(b)
  }
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const save = async () => {
    if (!f.imageUrl.trim()) return toast('Image URL is required', 'red')
    setSaving(true)
    const res = editing === 'new' ? await addBanner(f) : await updateBanner(editing.id, f)
    setSaving(false)
    if (res?.error) return toast(res.error, 'red')
    toast('Banner saved', 'green')
    setEditing(null)
  }

  const remove = async (b) => {
    if (!window.confirm('Delete this banner?')) return
    const res = await deleteBanner(b.id)
    toast(res?.error || 'Banner deleted', res?.error ? 'red' : 'default')
  }

  return (
    <main className="page">
      <div className="container page-narrow">
        <Link to="/admin" className="pep-back">← Site Admin</Link>
        <div className="between wrap" style={{ marginBottom: 16 }}>
          <div>
            <h1 className="section-title">Calculator Banners</h1>
            <p className="section-sub mb-0">Images/ads that rotate at the top of the calculator page.</p>
          </div>
          {!editing && <button className="btn btn-primary btn-sm" onClick={startNew} disabled={!configured}>+ New banner</button>}
        </div>

        {!configured && (
          <div className="config-notice" style={{ marginBottom: 18 }}>
            <strong>Backend not connected.</strong>
            <p>Connect Supabase and run <code>supabase/site.sql</code> to manage banners.</p>
          </div>
        )}

        {editing ? (
          <div className="card card-pad">
            <Field label="Desktop banner *" hint="upload a file or paste a URL">
              <div className="banner-upload">
                <label className="btn btn-soft btn-sm" style={{ cursor: 'pointer' }}>
                  {uploadingField === 'imageUrl' ? 'Uploading…' : '⬆ Upload image'}
                  <input type="file" accept="image/*" onChange={onPickFile('imageUrl')} disabled={!!uploadingField} style={{ display: 'none' }} />
                </label>
                <input className="input" value={f.imageUrl} onChange={set('imageUrl')} placeholder="…or paste an image URL" />
              </div>
              <p className="banner-hint">
                Best size: <strong>1600 × 400 px</strong> (wide, 4:1). Shows on tablets & desktops at full width, no
                cropping. JPG / PNG / WebP, under 5 MB.
              </p>
            </Field>
            {f.imageUrl && <img src={f.imageUrl} alt="" className="banner-preview" />}

            <Field label="Mobile banner" hint="optional — shown on phones instead of the desktop image">
              <div className="banner-upload">
                <label className="btn btn-soft btn-sm" style={{ cursor: 'pointer' }}>
                  {uploadingField === 'mobileImageUrl' ? 'Uploading…' : '⬆ Upload mobile image'}
                  <input type="file" accept="image/*" onChange={onPickFile('mobileImageUrl')} disabled={!!uploadingField} style={{ display: 'none' }} />
                </label>
                <input className="input" value={f.mobileImageUrl} onChange={set('mobileImageUrl')} placeholder="…or paste an image URL" />
              </div>
              <p className="banner-hint">
                Best size: <strong>1000 × 500 px</strong> (2:1) — taller so the artwork stays readable on a narrow
                screen. Used below 640 px wide. Leave blank to use the desktop image on phones too.
              </p>
            </Field>
            {f.mobileImageUrl && <img src={f.mobileImageUrl} alt="" className="banner-preview banner-preview-mobile" />}

            <Field label="Link URL" hint="where clicking the banner goes (optional)">
              <input className="input" value={f.linkUrl} onChange={set('linkUrl')} placeholder="https://…" />
            </Field>
            <Field label="Caption" hint="optional overlay text">
              <input className="input" value={f.caption} onChange={set('caption')} />
            </Field>
            <Field label="UTM tags" hint="tracks this banner in the destination's analytics">
              <input className="input" value={f.utm} onChange={set('utm')} placeholder="utm_source=calculatemypeptides&utm_content=bpc-banner" />
              <p className="banner-hint">
                Added to the link when clicked. Visitors arriving from an AI link (e.g. <code>?utm_source=chatgpt</code>)
                also pass their tags through automatically — so the sponsor's checkout can attribute the sale.
              </p>
            </Field>
            <div className="grid-2">
              <Field label="Sort order" hint="lower shows first"><input className="input" type="number" value={f.sortOrder} onChange={set('sortOrder')} /></Field>
              <label className="row" style={{ gap: 10, alignItems: 'center', marginTop: 30 }}>
                <input type="checkbox" checked={!!f.active} onChange={set('active')} style={{ width: 18, height: 18 }} />
                <span className="soft" style={{ fontWeight: 600 }}>Active (visible)</span>
              </label>
            </div>
            <div className="row" style={{ gap: 10, marginTop: 12 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save banner'}</button>
              <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        ) : banners.length === 0 ? (
          <div className="card empty">
            <div className="emoji">🖼️</div>
            <h3 style={{ marginBottom: 6 }}>No banners yet</h3>
            <p style={{ marginBottom: 0 }}>Add one to show a rotating image/ad on the calculator page.</p>
          </div>
        ) : (
          <div className="admin-list">
            {banners.map((b) => (
              <div className="card admin-row" key={b.id}>
                <div className="row" style={{ gap: 12, minWidth: 0 }}>
                  <img src={b.image_url} alt="" className="banner-thumb" />
                  <div style={{ minWidth: 0 }}>
                    <div className="row" style={{ gap: 8 }}>
                      <span className="pill">{b.sort_order}</span>
                      {b.active ? <span className="pill green">Active</span> : <span className="pill">Hidden</span>}
                    </div>
                    <div className="muted small" style={{ marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.caption || b.image_url}</div>
                  </div>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit(b)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => remove(b)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  )
}

function Field({ label, hint, children }) {
  return <div className="field"><label className="label">{label} {hint && <span className="hint">({hint})</span>}</label>{children}</div>
}
